import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from 'src/@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from 'src/@/components/ui/dialog';
import formulariosService from 'src/services/formulariosService';
import useAuth from 'src/hooks/useAuth';
import { useToast } from 'src/@/components/ui/use-toast';
import config from 'src/config';
import { FORMULARIOS_DESIGN as D } from './Formularios.design';

export default function FormulariosPublicos() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);

  useEffect(() => {
    if (user?.clienteId) {
      loadForms();
    }
  }, [user?.clienteId]);

  const loadForms = async () => {
    try {
      setLoading(true);
      const response = await formulariosService.listar(user?.clienteId);
      if (!response.error) {
        setForms(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar formulários:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os formulários',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNewForm = async () => {
    try {
      setCreatingDraft(true);
      const response = await formulariosService.criarDraft(user?.clienteId, user?.id);
      if (!response.error && response.data?._id) {
        navigate(`/dashboard/formularios/builder/${response.data._id}`);
      } else {
        toast({
          title: 'Erro',
          description: 'Não foi possível criar o formulário',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Erro ao criar formulário:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível criar o formulário',
        variant: 'destructive',
      });
    } finally {
      setCreatingDraft(false);
    }
  };

  const filteredForms = useMemo(
    () =>
      forms.filter((form) =>
        form.title?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [forms, searchTerm]
  );

  const handleDelete = async () => {
    if (!formToDelete) return;
    try {
      const response = await formulariosService.deletar(formToDelete._id);
      if (!response.error) {
        setForms(forms.filter((f) => f._id !== formToDelete._id));
        toast({
          title: 'Sucesso',
          description: 'Formulário deletado com sucesso',
        });
      }
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível deletar o formulário',
        variant: 'destructive',
      });
    } finally {
      setDeleteDialogOpen(false);
      setFormToDelete(null);
    }
  };

  const handleDuplicate = async (form) => {
    try {
      const response = await formulariosService.duplicar(form._id, user?.id);
      if (!response.error && response.data) {
        setForms([response.data, ...forms]);
        toast({
          title: 'Sucesso',
          description: 'Formulário duplicado com sucesso',
        });
      }
    } catch (error) {
      console.error('Erro ao duplicar:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível duplicar o formulário',
        variant: 'destructive',
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getPublicUrl = (publicId) => {
    const baseUrl = config.dev ? config.url_dev_links : config.url_prod_links;
    return `${baseUrl}/forms/${publicId}`;
  };

  return (
    <div className={D.root} style={{ fontFamily: D.fontStack }}>
      <div className={D.stickyHeader}>
        <div className={D.container}>
          <div className={D.headerRow}>
            <h1 className={D.title}>Formulários</h1>

            <div className={D.searchRow}>
              <div className={D.searchField}>
                <Input
                  className={D.searchInput}
                  placeholder="Pesquisar por título..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className={D.searchIconBtn}>
                  <Iconify icon="eva:search-outline" width={18} />
                </span>
              </div>
            </div>

            <div className={D.topbarActions}>
              <button
                type="button"
                className={D.iconButton}
                onClick={loadForms}
                title="Recarregar"
              >
                <Iconify icon="eva:refresh-outline" width={16} />
              </button>
              <button
                type="button"
                className={D.primaryButton}
                onClick={handleNewForm}
                disabled={creatingDraft}
              >
                {creatingDraft ? 'Criando...' : 'Novo formulário'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={D.body}>
        {loading ? (
          <div className={D.cardFlush}>
            <div className={D.loadingWrap}>
              <Iconify icon="mdi:loading" className={D.loadingIcon} />
              <p className="text-[13px] font-medium text-[#71717a]">Carregando formulários...</p>
            </div>
          </div>
        ) : filteredForms.length === 0 ? (
          <div className={D.emptyState}>
            <div className={D.emptyIcon}>
              <Iconify icon="eva:inbox-outline" width={28} />
            </div>
            <h3 className={D.emptyTitle}>Nenhum formulário encontrado</h3>
            <p className={D.emptyDesc}>
              {searchTerm.trim()
                ? 'Nenhum formulário corresponde à pesquisa.'
                : 'Você ainda não criou nenhum formulário.'}
            </p>
            {!searchTerm.trim() && (
              <button type="button" className={`mt-5 ${D.link}`} onClick={handleNewForm}>
                Criar formulário agora
              </button>
            )}
          </div>
        ) : (
          <div className={D.cardFlush}>
            <div className={D.tableWrap}>
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#f0f0f3] hover:bg-transparent">
                    <TableHead className={D.tableHead}>Título</TableHead>
                    <TableHead className={D.tableHead}>Status</TableHead>
                    <TableHead className={D.tableHead}>Tipo</TableHead>
                    <TableHead className={D.tableHead}>ID público</TableHead>
                    <TableHead className={D.tableHead}>Perguntas</TableHead>
                    <TableHead className={D.tableHead}>Criado em</TableHead>
                    <TableHead className={`${D.tableHead} text-right`}>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredForms.map((form) => (
                    <TableRow key={form._id} className={D.tableRow}>
                      <TableCell className={`${D.tableCell} font-semibold`}>
                        {form.title || 'Sem título'}
                      </TableCell>
                      <TableCell>
                        {form.isPublished ? (
                          <span className={`${D.badge} ${D.badgeOk}`}>
                            <span className={`${D.badgeDot} ${D.badgeDotOk}`} />
                            Publicado
                          </span>
                        ) : (
                          <span className={`${D.badge} ${D.badgeWarn}`}>
                            <span className={`${D.badgeDot} ${D.badgeDotWarn}`} />
                            Rascunho
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {form.isStepByStep ? (
                          <span className={`${D.badge} ${D.badgeSoft}`}>Step-by-step</span>
                        ) : (
                          <span className={D.tableCellMuted}>Padrão</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {form.publicId ? (
                          <div className="flex items-center gap-1.5">
                            <code className="text-[12px] text-[#71717a] bg-[#f4f4f5] px-1.5 py-0.5 rounded-md font-mono">
                              {form.publicId}
                            </code>
                            <button
                              type="button"
                              className={D.iconButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(getPublicUrl(form.publicId));
                                toast({ title: 'Link copiado!' });
                              }}
                              title="Copiar link"
                            >
                              <Iconify icon="mdi:content-copy" width={14} />
                            </button>
                          </div>
                        ) : (
                          <span className={D.tableCellMuted}>—</span>
                        )}
                      </TableCell>
                      <TableCell className={D.tableCellMuted}>
                        {form.questions?.length || 0}
                      </TableCell>
                      <TableCell className={D.tableCellMuted}>
                        {formatDate(form.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            className={D.iconButton}
                            onClick={() => navigate(`/dashboard/formularios/builder/${form._id}`)}
                            title="Editar"
                          >
                            <Iconify icon="eva:edit-2-outline" width={16} />
                          </button>
                          <button
                            type="button"
                            className={D.iconButton}
                            onClick={() =>
                              navigate(`/dashboard/formularios/builder/${form._id}?tab=respostas`)
                            }
                            title="Ver respostas"
                          >
                            <Iconify icon="eva:message-circle-outline" width={16} />
                          </button>
                          <button
                            type="button"
                            className={D.iconButton}
                            onClick={() => handleDuplicate(form)}
                            title="Duplicar"
                          >
                            <Iconify icon="mdi:content-copy" width={16} />
                          </button>
                          {form.isPublished && (
                            <button
                              type="button"
                              className={D.iconButton}
                              onClick={() => window.open(getPublicUrl(form.publicId), '_blank')}
                              title="Abrir formulário"
                            >
                              <Iconify icon="eva:external-link-outline" width={16} />
                            </button>
                          )}
                          <button
                            type="button"
                            className={D.iconButtonDanger}
                            onClick={() => {
                              setFormToDelete(form);
                              setDeleteDialogOpen(true);
                            }}
                            title="Excluir"
                          >
                            <Iconify icon="eva:trash-2-outline" width={16} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className={D.tableFooter}>
              Mostrando {filteredForms.length} de {forms.length} formulários
            </div>
          </div>
        )}
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-[12px] border border-[#e7e7ec] bg-white p-0 gap-0 shadow-none">
          <DialogHeader className="px-[26px] pt-[26px] pb-0">
            <DialogTitle className={D.dialogTitle}>Confirmar exclusão</DialogTitle>
            <DialogDescription className={`${D.dialogDesc} pt-2`}>
              Tem certeza que deseja excluir o formulário{' '}
              <span className="font-semibold text-[#18181b]">
                &quot;{formToDelete?.title}&quot;
              </span>
              ?
              <span className="block mt-2 text-[12px] text-[#b42318]">
                Esta ação é irreversível e todas as respostas serão perdidas.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="px-[26px] py-[22px] gap-2.5 sm:gap-2.5">
            <button
              type="button"
              className={D.ghostButton}
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancelar
            </button>
            <button type="button" className={D.dangerButton} onClick={handleDelete}>
              Excluir formulário
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
